import { Plus, SlidersHorizontal } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { useFormDraft } from '@/components/ui/useFormDraft'
import { cn } from '@/lib/cn'
import {
  legDraftFrom,
  toLegPayload,
  useCaseExpenseLines,
  useRecordExpenseBatch,
  useSetExpenseDetailOf,
  useUpdateLeg,
  type PlannedExpenseLineInput,
} from '@/features/cases/api/useCase'
import { CarrierSelect } from '@/features/cases/components/CarrierSelect'
import { refusalMessage } from '@/features/cases/lib/refusal'
import { CarrierPaymentSheet } from '@/features/finance/components/CarrierPaymentSheet'
import { normaliseDecimal } from '@/features/finance/lines'
import type { CaseLeg, LegVehiclePayload, VehicleMoney } from '@/types/domain'

import { legIdentityHidden } from './identity'
import { VehicleTableRows } from './VehicleTableRows'

/* «Фуры и ставки» — the direction's trucks, entered the way the desk actually
 * enters them (FR-67, issue #35 story 6).
 *
 * ── Why this is a spreadsheet and not six sheets ────────────────────────────
 *
 * A direction is routinely run by six trucks booked in one phone call, and the
 * facts that differ between them are four short strings and a number. The sheet
 * this replaces asked for one truck at a time and asked for a plate only, which
 * is why the plates were being kept in the chat and the rates in somebody's
 * notebook. The defaults strip on top is the other half of the same
 * observation: the carrier and the rate are usually the SAME for all six, and
 * typing them six times is how the sixth one ends up different by accident.
 *
 * ── One press, two writes, and the honest report when only one lands ────────
 *
 * Saving is a leg PUT with the whole merged truck list, then ONE batch of
 * planned cost lines for the new trucks that carry a rate. It cannot be one
 * request: the trucks are casefile's and the money is finance's (AD-3), and the
 * expense line has to name the vehicle id the PUT has only just minted.
 *
 * So the failure between them is REAL and is reported as what it is — the
 * trucks are saved, the rates are not — with a retry that re-sends the rates
 * alone. Anything else would be a lie in one of the two directions: claiming
 * the save failed would have somebody re-enter six trucks that are already
 * there, and claiming it worked would leave a direction whose money is silently
 * missing.
 *
 * ── What this table does NOT edit ───────────────────────────────────────────
 *
 * A SAVED truck's rate. The rate is a planned cost line on the case's money
 * table, and it is edited there — this column writes one only when the truck is
 * new. Making it editable here would mean this table deciding which of the
 * AD-4 rows an edit meant, which is precisely the guess `useAmendExpense`
 * exists so that no client has to make.
 */

/** One row of the editor — a saved truck or one being added. */
export interface VehicleRow {
  /** React's key, stable across edits so retyping a row above does not move
   *  the focus. Never the wire's. */
  key: string
  /** The saved vehicle's id, `''` for a truck that does not exist yet. It is
   *  what tells a rate to be written: only a NEW truck's rate is booked here. */
  id: string
  plate: string
  trailerPlate: string
  containerNumber: string
  cmrNumber: string
  carrierId: string
  carrierName: string
  driverName: string
  driverPhone: string
  /** Whole kilograms as typed. A string in the draft and a number on the wire:
   *  a half-typed "18 4" is not a number and must not become one. */
  weightKg: string
  /** The agreed rate, in the defaults strip's currency. Read-only on a saved
   *  truck (see the file header) and blank on one whose rate nobody set. */
  rate: string
  /** Saved rows render read-only until somebody clicks them; new rows are born
   *  editable and stay that way. */
  editing: boolean
}

const CURRENCIES = ['USD', 'EUR', 'CNY', 'RUB', 'TMT'] as const

/** Accepts `1234.56`, `1 234,56`; rejects text and blanks — the same shape the
 *  finance sheets accept, because it lands in the same column. */
function isRate(raw: string): boolean {
  const normalised = normaliseDecimal(raw)
  return normalised !== '' && /^\d+(\.\d{1,2})?$/.test(normalised)
}

/** Whole kilograms, which is what the column is (0055). A thousands separator
 *  or a decimal is a typo here rather than a format to be parsed: the schema
 *  stores an integer, and guessing that «18.4» meant 18 400 would write a
 *  weight nobody typed onto a CMR. */
function isWeight(raw: string): boolean {
  return /^\d+$/.test(raw.trim())
}

/** What a row cannot send, per field.
 *
 *  A ROW-LEVEL check rather than two filters at save time, and that is the
 *  whole subject of this pair of predicates: both fields used to be dropped
 *  silently by the composers below — a rate that did not parse simply did not
 *  become a cost line, and a weight that did not parse became `undefined`,
 *  which on a whole-record PUT ERASES the kilograms already stored against the
 *  truck. Both are the "money (or fact) silently missing" outcome this file's
 *  header says it exists to prevent, arriving by the one route nobody watches:
 *  a save that reports success.
 *
 *  Blank is not a problem in either field. An empty rate is a truck whose price
 *  is not agreed yet, and an empty weight is a weight nobody has weighed. */
function rowProblems(row: VehicleRow): { rate: boolean; weight: boolean } {
  // Rows with no plate are dropped whole (see mergedVehicles) — an untouched
  // repeater row is an artefact of the widget and complaining about it would
  // make the "add three rows" button produce three errors.
  if (row.plate.trim() === '') return { rate: false, weight: false }

  return {
    // Only a NEW truck's rate is written from here (see the file header), so a
    // saved row's blanked rate input is not a claim about anything.
    rate: row.id === '' && row.rate.trim() !== '' && !isRate(row.rate),
    weight: row.weightKg.trim() !== '' && !isWeight(row.weightKg),
  }
}

/** The plate as the server matches it: upsert-by-plate folds case and trims
 *  (0024's unique index), so the map back from the PUT's answer to the row that
 *  produced it has to fold identically. A truck typed «tm-ah 1234» on Thursday
 *  and «TM-AH 1234» on Monday is one truck, and a rate booked against a second
 *  id would be a rate on a vehicle that does not exist. */
function plateKey(plate: string): string {
  return plate.trim().toUpperCase()
}

function emptyRow(
  key: string,
  defaults: { carrierId: string; carrierName: string; rate: string },
): VehicleRow {
  return {
    key,
    id: '',
    plate: '',
    trailerPlate: '',
    containerNumber: '',
    cmrNumber: '',
    /* PREFILLED from the strip, and editable afterwards — the whole point of
       the strip is that the sixth truck's carrier is usually the first's. What
       it is not is a default applied at SAVE time: a value the user can see
       and correct is a value they agreed to. */
    carrierId: defaults.carrierId,
    carrierName: defaults.carrierName,
    driverName: '',
    driverPhone: '',
    weightKg: '',
    rate: defaults.rate,
    editing: true,
  }
}

/** A saved truck, flattened for editing.
 *
 *  A suppressed value never loads into an input (AD-6) — `legDraftFrom` states
 *  the rule for the leg's own carrier and it holds one level down: a form that
 *  seeded the marker would write it back as though somebody had typed it. Here
 *  the four identity fields are blanked, and the SAVE is what is blocked
 *  instead; see `identityHidden` below. */
function savedRow(vehicle: CaseLeg['vehicles'][number]): VehicleRow {
  const editable = (value: unknown): string =>
    typeof value === 'string' ? value : ''

  return {
    key: vehicle.id,
    id: vehicle.id,
    plate: vehicle.plate,
    trailerPlate: vehicle.trailerPlate ?? '',
    containerNumber: vehicle.containerNumber ?? '',
    cmrNumber: vehicle.cmrNumber ?? '',
    carrierId: editable(vehicle.carrierId),
    carrierName: editable(vehicle.carrierName),
    driverName: editable(vehicle.driverName),
    driverPhone: editable(vehicle.driverPhone),
    weightKg: vehicle.weightKg === undefined ? '' : String(vehicle.weightKg),
    /* Blank rather than the served figure. The saved money columns render the
       USD the server booked; this input exists only for a NEW truck, and
       putting a converted figure into it would invite an edit that writes a
       second cost line for a rate that is already recorded. */
    rate: '',
    editing: false,
  }
}

export function VehiclesTable({ caseId, leg }: { caseId: string; leg: CaseLeg }) {
  const { t } = useTranslation()

  const updateLeg = useUpdateLeg(caseId)
  const batch = useRecordExpenseBatch(caseId)

  const vehicles = leg.vehicles ?? []
  const perVehicle = new Map<string, VehicleMoney>(
    (leg.money?.perVehicle ?? []).map((row) => [row.vehicleId, row]),
  )

  /* The AD-6 guard, shared with the page's edit card rather than restated here:
     both submit the same whole-record PUT, so both are refused by the same
     condition and both say so in the same sentence. See `./identity`. */
  const identityHidden = legIdentityHidden(leg)

  /* Which SAVED truck the pay sheet is open on, or null for closed. The act
     lives on this table rather than on the money card because the reader
     deciding to pay is looking at the truck's row — its rate, its advance, its
     paid figure are the three columns to the left of the button. The card
     keeps the header act for paying without implying a truck. Gated on the
     money block the same way the three columns are: a leg whose money the
     composer could not assemble has no debt figure for the sheet to seed. */
  const [adding, setAdding] = useState(false)
  const [editingTable, setEditingTable] = useState(false)
  const [payingVehicleId, setPayingVehicleId] = useState<string | null>(null)
  const canPay = leg.money !== undefined

  const [defaults, setDefaults] = useState({
    carrierId: '',
    carrierName: '',
    rate: '',
    currency: 'USD',
  })

  /* Re-seeded whenever the SET of saved trucks changes, which is exactly when a
     draft row has become a saved one: the batch's invalidation refetches the
     leg, the ids arrive, and the rows the user just typed stop being drafts.
     Keyed on the ids rather than on the leg alone, because the leg's key never
     changes and the table would go on showing six blue rows over six saved
     trucks — and keyed on the ids rather than the count, so a truck swapped for
     another re-seeds too. An unrelated refetch does not disturb typing. */
  const [rows, setRows] = useFormDraft<VehicleRow[]>(
    `${leg.id}:${vehicles.map((vehicle) => vehicle.id).join(',')}`,
    () => vehicles.map(savedRow),
  )

  /* The rates that were composed for a save whose PUT landed and whose batch
     did not. Held so the retry re-sends THE SAME lines rather than recomposing
     them from rows that are now saved and would compose nothing. */
  const [unwrittenRates, setUnwrittenRates] = useState<PlannedExpenseLineInput[]>([])

  const drafts = rows.filter((row) => row.id === '')
  const trucksUnchanged =
    rows.every((row) => !row.editing) || rows.every((row) => row.plate.trim() === '')

  /* Which generalized plan line these trucks' rates DETAIL (spec
     2026-09-01-expense-detail-grouping, L7). The quote carries the movement
     once — «Transportation service 8 709» — and this table writes it again per
     truck; without the link the case plans the same freight twice and the
     forecast is wrong by the whole of it. The link is not a field of the leg
     (AD-3: casefile holds no money), it is `detailOf` on each per-truck cost
     line, and the select's current answer is READ BACK from those lines. */
  const expenseLines = useCaseExpenseLines(caseId)
  const setDetailOf = useSetExpenseDetailOf(caseId)
  const lines = expenseLines.data ?? []

  /* A row may stand as a parent when it is a top-level unattributed plan of
     this case (spec L3) — the same three facts the server checks before it
     accepts the link, so the list cannot offer a choice the save would refuse. */
  const candidates = lines.filter(
    (line) => line.legId === undefined && line.detailOf === undefined && !line.unplanned,
  )
  /* This leg's own per-truck plans carry the current answer. Read as a single
     answer only when they AGREE: rows filed under two different parents are a
     state no select can represent, and showing one of them would re-file the
     others behind the reader's back on the next save. */
  const legPlans = lines.filter(
    (line) => line.legId === leg.id && line.vehicleId !== undefined,
  )
  const currentParent =
    legPlans.length > 0 &&
    legPlans.every((line) => line.detailOf === legPlans[0]?.detailOf)
      ? (legPlans[0]?.detailOf ?? '')
      : ''

  /* `null` means untouched, and the default is DERIVED rather than seeded into
     state: the list arrives after the first paint, and a state seeded from an
     empty list would hold '' for the rest of the visit. */
  const [parentChoice, setParentChoice] = useState<string | null>(null)
  const chosenParent =
    parentChoice ?? (currentParent || (candidates.length === 1 ? candidates[0]!.id : ''))

  /* Moving the choice is a saveable change on its own, and the save must not
     stay gated on a truck edit: L8's path for a case that predates the column
     is open the direction, pick the parent, save — with nothing else touched. */
  const parentMoved = legPlans.length > 0 && chosenParent !== currentParent
  const nothingToSave = trucksUnchanged && !parentMoved

  /* The per-field complaints, computed on every render rather than on submit.
     A malformed figure is visible while it is being typed, and the SAVE is
     blocked by it — the alternative, which this table shipped with, was to drop
     the field quietly and report a successful save over a rate that was never
     recorded and a weight that had just been erased. */
  const problems = new Map(rows.map((row) => [row.key, rowProblems(row)]))
  const badRates = rows.filter((row) => problems.get(row.key)?.rate)
  const badWeights = rows.filter((row) => problems.get(row.key)?.weight)
  const fieldsInvalid = badRates.length > 0 || badWeights.length > 0

  const update = (key: string, patch: Partial<VehicleRow>) =>
    setRows(rows.map((row) => (row.key === key ? { ...row, ...patch } : row)))

  /** The whole list as the leg PUT takes it: saved trucks and drafts together.
   *
   *  WHOLE, because the write is whole — a payload carrying only the new rows
   *  would delete the trucks already on the direction. Rows nobody typed a
   *  plate into are dropped rather than sent for the server to refuse, which is
   *  `toLegPayload`'s own treatment of an untouched repeater row: an empty row
   *  is an artefact of the widget, not a claim somebody made. */
  function mergedVehicles(): LegVehiclePayload[] {
    return rows.filter((row) => row.plate.trim() !== '').map(vehiclePayloadOf)
  }

  /** One row as the PUT takes it — shared by the whole-list save and the
   *  single-truck removal, so the two cannot disagree about what a blank
   *  field means. */
  function vehiclePayloadOf(row: VehicleRow): LegVehiclePayload {
    return {
      plate: row.plate.trim(),
      trailerPlate: row.trailerPlate.trim() || undefined,
      containerNumber: row.containerNumber.trim() || undefined,
      cmrNumber: row.cmrNumber.trim() || undefined,
      carrierId: row.carrierId.trim() || undefined,
      carrierName: row.carrierName.trim() || undefined,
      driverName: row.driverName.trim() || undefined,
      driverPhone: row.driverPhone.trim() || undefined,
      // Unparseable weights cannot reach here: `problems` blocks the save.
      // The guard stays as the belt — `undefined` on this whole-record PUT
      // is an ERASURE, and it must never be reached by accident.
      weightKg: isWeight(row.weightKg) ? Number(row.weightKg.trim()) : undefined,
    }
  }

  /** Removes one truck.
   *
   *  A DRAFT row is the widget's own — nothing is saved, so nothing is asked
   *  of the server and the row simply leaves the list.
   *
   *  A SAVED row is a leg PUT carrying the SAVED trucks minus this one — never
   *  `mergedVehicles()`, because a delete press must not also save the
   *  half-typed draft rows sitting below. No confirmation dialog, and that is
   *  a decision: a truck with money on it cannot be deleted at all — 0056's
   *  ON DELETE RESTRICT comes back as a refusal naming the plate, rendered in
   *  the same error area a refused save uses — and a truck without money is
   *  four short fields to re-enter. The refusal is the guard, not a dialog. */
  function removeTruck(row: VehicleRow) {
    if (row.id === '') {
      setRows(rows.filter((other) => other.key !== row.key))
      return
    }

    void updateLeg
      .mutateAsync({
        legId: leg.id,
        payload: {
          ...toLegPayload(legDraftFrom(leg)),
          vehicles: rows
            .filter((other) => other.id !== '' && other.id !== row.id)
            .map(vehiclePayloadOf),
        },
      })
      /* Caught for `save()`'s reason: the banner below already renders
         `updateLeg.error`, and an uncaught rejection on top of it is console
         noise reporting nothing the reader is not already told. */
      .catch(() => undefined)
  }

  /** The planned cost lines the new trucks' rates become.
   *
   *  Keyed back to the vehicle ids the PUT answered with — the rate cannot be
   *  written before the truck exists, and inventing the id would be inventing
   *  the row it points at. A rate typed on a row whose plate the server did not
   *  answer with is DROPPED rather than posted against a guess; that can only
   *  happen if the plate was rejected, in which case the PUT failed and this is
   *  not reached at all.
   *
   *  No vendor travels with it: the server refuses a planned line that carries
   *  one (`planned-has-no-vendor`), because a vendor is who invoiced and
   *  nobody has. Who runs the truck is on the truck's own row. */
  function plannedRates(saved: CaseLeg): PlannedExpenseLineInput[] {
    const idByPlate = new Map(
      (saved.vehicles ?? []).map((vehicle) => [plateKey(vehicle.plate), vehicle.id]),
    )

    return drafts
      .filter((row) => row.plate.trim() !== '' && isRate(row.rate))
      .flatMap((row) => {
        const vehicleId = idByPlate.get(plateKey(row.plate))
        if (vehicleId === undefined) return []
        return [
          {
            kind: 'planned' as const,
            item: t('cases.legs.vehiclesTable.rateItem', { plate: row.plate.trim() }),
            legId: leg.id,
            vehicleId,
            amount: {
              amount: normaliseDecimal(row.rate),
              currency: defaults.currency,
            },
            /* Omitted rather than sent empty when nothing is being detailed:
               the server parses a present `detailOf` and an empty string is
               not a uuid. */
            ...(chosenParent === '' ? {} : { detailOf: chosenParent }),
          },
        ]
      })
  }

  async function save() {
    let savedVehicles = vehicles
    /* The leg PUT is skipped when no truck was touched. It replaces the WHOLE
       record, and re-submitting an unchanged truck list merely to satisfy a
       select the user moved is exactly the write this component's other
       comments warn about — a whole-record PUT is how fields get erased. */
    if (!trucksUnchanged) {
      const saved = await updateLeg.mutateAsync({
        legId: leg.id,
        /* Every other field of the leg, unchanged. The PUT replaces the WHOLE
           record, so a payload built from the truck list alone would clear the
           carrier, the dates and the dispatcher's stall note — the failure
           `legDraftFrom` was extracted to prevent, reused here rather than
           re-derived. */
        payload: { ...toLegPayload(legDraftFrom(leg)), vehicles: mergedVehicles() },
      })

      savedVehicles = saved.vehicles ?? []
      const rates = plannedRates(saved)
      if (rates.length > 0) {
        // Held BEFORE the call, so a failure has the lines to retry with.
        setUnwrittenRates(rates)
        await batch.mutateAsync(rates)
        setUnwrittenRates([])
      }
    }

    /* Moving the choice carries the rates ALREADY written on this leg with it
       (spec L8 — the path by which an old case is brought under a group). One
       PATCH per line, one field each: the money on those lines is served in
       its booked currency and must not be re-sent from a screen that only
       changed a grouping. */
    if (parentMoved) {
      await Promise.all(
        legPlans.map((line) =>
          setDetailOf.mutateAsync({
            lineId: line.id,
            detailOf: chosenParent === '' ? null : chosenParent,
          }),
        ),
      )
    }
    setRows(savedVehicles.map(savedRow))
    setAdding(false)
    setEditingTable(false)
    setParentChoice(null)
  }

  async function retryRates() {
    await batch.mutateAsync(unwrittenRates)
    setUnwrittenRates([])
  }

  const pending = updateLeg.isPending || batch.isPending || setDetailOf.isPending
  /* The two failures are DIFFERENT sentences. A refused PUT means nothing was
     written and the user can correct and press again; a refused batch means the
     trucks are on the direction and their rates are not, which is a different
     thing to do next. */
  const ratesFailed = unwrittenRates.length > 0 && batch.error !== null

  const editorOpen = editingTable || adding || rows.some((row) => row.editing)
  const addRows = (count: number) => setRows([
    ...rows,
    ...Array.from({ length: count }, (_, index) =>
      emptyRow('draft-' + Date.now() + '-' + (rows.length + index), defaults)),
  ])
  const cancel = () => {
    setRows(vehicles.map(savedRow))
    setParentChoice(null)
    setAdding(false)
    setEditingTable(false)
  }

  return (
    <Card>
      <CardHeader className="flex-wrap gap-3 px-5 pt-5 sm:px-6"
        action={
          <div className="flex flex-wrap gap-2">
            {!editorOpen ? <Button variant="ghost" size="sm" disabled={identityHidden}
              onClick={() => setEditingTable(true)}>
              {t('common.actions.edit')}
            </Button> : null}
            {/* Adding a truck is the bar under the table now; this reveals
                the fields the next new rows are BORN with. Named for that and
                not for the adding, or the two controls read as the same act
                in two places. */}
            <Button variant="secondary" size="sm" iconLeft={SlidersHorizontal}
              disabled={identityHidden || pending} aria-expanded={adding}
              onClick={() => setAdding(!adding)}>
              {t('cases.legs.vehiclesTable.sharedFields')}
            </Button>
          </div>
        }
      >
        <CardTitle className="flex flex-wrap items-center gap-3 text-[18px] font-semibold">
          {t('cases.legs.vehiclesTable.title')}
          <span className="text-text-muted text-[13px] font-normal">
            {t('cases.legFinance.trucks', { count: vehicles.length })}
          </span>
        </CardTitle>
        {!editorOpen && legPlans.length > 0 ? (
          <p className="text-text-muted text-[13px]">
            {t('cases.legs.vehiclesTable.planArticle')}: {
              currentParent
                ? lines.find((line) => line.id === currentParent)?.description
                : legPlans.some((line) => line.detailOf)
                  ? t('cases.legs.vehiclesTable.mixedPlans')
                  : t('cases.legs.vehiclesTable.detailOfNone')
            }
          </p>
        ) : null}
      </CardHeader>

      <CardBody className="px-0 pt-4 pb-0">
        {/* The defaults strip. It fills NEW rows and nothing else — it is not a
            value on the direction, and pressing save does not apply it to the
            trucks already there. */}
        {adding ? <div className="bg-surface-sunken mx-5 mb-4 flex flex-wrap items-end gap-3 rounded-md border border-border-subtle p-4 sm:mx-6">
          <label className="flex min-w-52 flex-1 flex-col gap-1">
            <span className="asa-eyebrow">
              {t('cases.legs.vehiclesTable.defaultCarrier')}
            </span>
            <CarrierSelect
              size="sm"
              value={defaults.carrierId}
              name={defaults.carrierName}
              disabled={identityHidden}
              onChange={(carrier) =>
                setDefaults({
                  ...defaults,
                  carrierId: carrier.id,
                  carrierName: carrier.name,
                })
              }
            />
          </label>

          <label className="flex w-36 flex-col gap-1">
            <span className="asa-eyebrow">
              {t('cases.legs.vehiclesTable.defaultRate')}
            </span>
            <Input
              inputSize="sm"
              inputMode="decimal"
              className="asa-num"
              value={defaults.rate}
              onChange={(event) =>
                setDefaults({ ...defaults, rate: event.target.value })
              }
            />
          </label>

          <label className="flex w-28 flex-col gap-1">
            <span className="asa-eyebrow">{t('cases.legs.vehiclesTable.currency')}</span>
            <Select
              selectSize="sm"
              value={defaults.currency}
              onChange={(currency) => setDefaults({ ...defaults, currency })}
              options={CURRENCIES.map((code) => ({ value: code, label: code }))}
              aria-label={t('cases.legs.vehiclesTable.currency')}
            />
          </label>

          <p className="text-text-muted pb-1.5 text-[12px] leading-snug font-light">
            {t('cases.legs.vehiclesTable.defaultsNote')}
          </p>
        </div> : null}

        {/* Which quote line these rates detail. One select for the table, not
            one per truck: the trucks on a direction detail the same line, and
            asking six times is how the answer stops being given. Absent
            entirely when the case has nothing generalized left to detail — an
            empty dropdown is a question with no answers. */}
        {candidates.length > 0 || currentParent !== '' ? (
          <label className="mx-5 mb-4 flex flex-wrap items-center gap-2 sm:mx-6">
            <span className="asa-eyebrow">
              {t('cases.legs.vehiclesTable.detailOfLabel')}
            </span>
            <Select
              selectSize="sm"
              className="min-w-64"
              value={chosenParent}
              onChange={(value) => setParentChoice(value)}
              aria-label={t('cases.legs.vehiclesTable.detailOfLabel')}
              options={[
                { value: '', label: t('cases.legs.vehiclesTable.detailOfNone') },
                ...candidates.map((line) => ({
                  value: line.id,
                  label: line.description,
                })),
                /* The line already chosen has itself stopped being a candidate
                   in one case — somebody made it a detail elsewhere — and a
                   select whose current value is not in its own options renders
                   blank, which reads as "not detailing" over a leg that is. */
                ...(currentParent !== '' &&
                !candidates.some((line) => line.id === currentParent)
                  ? lines
                      .filter((line) => line.id === currentParent)
                      .map((line) => ({ value: line.id, label: line.description }))
                  : []),
              ]}
            />
          </label>
        ) : null}

        {/* Why the save is unavailable, said BEFORE the table rather than after
            a press that does nothing. */}
        {identityHidden ? (
          <p className="text-text-muted mx-5 mb-4 text-[12.5px] leading-snug">
            {t('cases.legs.identityHidden')}
          </p>
        ) : null}

        {/* The malformed figures, named by column and counted. Above the table
            like the other two record-level sentences, because the save button
            is at the BOTTOM and a reader who has just found it disabled is
            looking for the reason at the top of the card. The offending inputs
            carry the red border too — see `invalid` on the cells. */}
        {fieldsInvalid ? (
          <p className="text-negative mx-5 mb-4 text-[12.5px] leading-snug" role="alert">
            {badRates.length > 0
              ? t('cases.legs.vehiclesTable.rateInvalid', { count: badRates.length })
              : null}{' '}
            {badWeights.length > 0
              ? t('cases.legs.vehiclesTable.weightInvalid', { count: badWeights.length })
              : null}
          </p>
        ) : null}

        {ratesFailed ? (
          <div className="border-line mx-5 my-4 flex flex-wrap items-center gap-3 border-t pt-3">
            <p className="text-negative min-w-0 flex-1 text-[12.5px] leading-snug">
              {t('cases.legs.vehiclesTable.savedTrucksRatesFailed', {
                count: unwrittenRates.length,
              })}{' '}
              {batch.error ? refusalMessage(batch.error, t) : null}
            </p>
            <Button
              variant="secondary"
              size="sm"
              disabled={batch.isPending}
              /* Caught exactly as `save()` is, and for the reason a second
                 failure makes plain: `mutateAsync` rejects, the banner beside
                 this button is already rendering that rejection from
                 `batch.error`, and an uncaught one on top of it is an unhandled
                 rejection in the console — noise that reports nothing the
                 reader is not already being told. */
              onClick={() => void retryRates().catch(() => undefined)}
            >
              {t('cases.legs.vehiclesTable.retryRates')}
            </Button>
          </div>
        ) : updateLeg.error ? (
          <p className="text-negative mx-5 mb-4 text-[12.5px] leading-snug">
            {refusalMessage(updateLeg.error, t)}
          </p>
        ) : setDetailOf.error ? (
          /* The re-filing PATCHes run last, so a refusal here means the trucks
             and their rates are SAVED and only the grouping did not move —
             a different sentence from either failure above, or the reader
             re-types rates that are already on the direction. */
          <p className="text-negative mx-5 mb-4 text-[12.5px] leading-snug">
            {t('cases.legs.vehiclesTable.detailOfFailed')}{' '}
            {refusalMessage(setDetailOf.error, t)}
          </p>
        ) : null}

        {rows.length === 0 ? (
          <p className="text-text-muted px-5 pb-5 text-[13px] sm:px-6">
            {t('cases.legPage.vehiclesEmpty')}
          </p>
        ) : (
          <div className="asa-scroll overflow-x-auto">
            <table className="block w-full border-collapse text-left md:table">
              <thead className="bg-surface-sunken max-md:hidden">
                <tr className="border-line border-y">
                  {(['transport', 'people', 'weight', 'rate', 'paid', 'actions'] as const).map((column) => (
                    <th key={column} scope="col" className={cn(
                      'text-text-muted px-3 py-3 text-[10px] font-medium tracking-wide uppercase first:pl-6 last:pr-6',
                      /* The acts sit flush right on their rows, so their
                         heading does too — a left-set «Actions» over a
                         right-set column reads as a heading for the figures
                         beside it. */
                      ['weight', 'rate', 'paid', 'actions'].includes(column) && 'text-right',
                    )}>
                      {t(`cases.legs.vehiclesTable.groupedColumns.${column}`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="block md:table-row-group">
                {rows.map((row, index) => <VehicleTableRows key={row.key} row={row} index={index}
                  vehicle={vehicles.find((vehicle) => vehicle.id === row.id)}
                  money={perVehicle.get(row.id)} identityHidden={identityHidden}
                  pending={pending} canPay={canPay}
                  problems={problems.get(row.key) ?? { rate: false, weight: false }}
                  onChange={(patch) => update(row.key, patch)}
                  onRemove={() => removeTruck(row)} onPay={() => setPayingVehicleId(row.id)} />)}
              </tbody>
            </table>
          </div>
        )}

        {/* The add-row bar, UNDER the last truck and always there.
            It used to live inside the defaults strip at the top of the card,
            which put it above six rows the reader had to scroll past to reach
            it and behind a toggle they had to find first. A sheet grows at the
            bottom, and this is the same gesture: the new row appears exactly
            where the button is. */}
        <div className="border-line flex flex-wrap items-center gap-2 border-t px-5 py-3 sm:px-6">
          <Button variant="secondary" size="sm" iconLeft={Plus}
            disabled={identityHidden || pending} onClick={() => addRows(1)}>
            {t('cases.legs.vehiclesTable.oneMoreRow')}
          </Button>
          <Button variant="ghost" size="sm" disabled={identityHidden || pending}
            onClick={() => addRows(3)}>
            {t('cases.legs.vehiclesTable.threeRows')}
          </Button>
        </div>

        {/* The foot of the card. Open while something is being edited, while a
            plan-line choice has moved (L8: pick the parent and save, with no
            truck touched), and on a leg whose identity is suppressed — there
            the disabled Save is the ANSWER to the sentence above the table,
            and a card that simply had no button would leave that sentence
            explaining a control the reader cannot see. */}
        {editorOpen || parentMoved || identityHidden ? (
          <div className="border-line flex flex-wrap items-center gap-3 border-t px-5 py-4 sm:px-6">
            {drafts.length > 0 ? <span className="text-text-muted flex-1 text-xs">
              {t('cases.legs.vehiclesTable.newTrucksNote', { count: drafts.length })}
            </span> : null}
            <div className="ml-auto flex gap-2">
              <Button variant="secondary" size="sm" disabled={pending} onClick={cancel}>
                {t('common.actions.cancel')}
              </Button>
              {!nothingToSave || identityHidden ? <Button size="sm" disabled={pending || identityHidden || fieldsInvalid || ratesFailed}
                onClick={() => void save().catch(() => undefined)}>
                {t('cases.legs.vehiclesTable.saveTrucks')}
              </Button> : null}
            </div>
          </div>
        ) : null}
      </CardBody>

      {/* The same one-request sheet the money card's header opens, here opened
          already knowing the truck. Rendered only while the money block exists
          — the same condition that puts the Pay buttons on the rows. */}
      {leg.money ? (
        <CarrierPaymentSheet
          open={payingVehicleId !== null}
          onClose={() => setPayingVehicleId(null)}
          caseId={caseId}
          legId={leg.id}
          vehicles={vehicles}
          expenses={leg.money.expenses}
          perVehicle={leg.money.perVehicle}
          defaultVehicleId={payingVehicleId ?? undefined}
          carrierName={leg.carrierName}
        />
      ) : null}
    </Card>
  )
}

